import React, { useState } from 'react';
import Icon from 'components/AppIcon';
import { TENDER_STATUS_CONFIG, CATEGORY_CONFIG } from './pipelineData';

const STEPPER_STEPS = ['Draft', 'Open', 'Evaluation', 'Awarded'];

function OverviewTab({ wp }) {
  const cat = CATEGORY_CONFIG?.[wp?.category];
  return (
    <div className="space-y-5">
      <div>
        <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Description</h4>
        <p className="text-sm text-slate-700 leading-relaxed">{wp?.description}</p>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <p className="text-xs text-slate-500 mb-1">Associated Deal</p>
          <p className="text-sm font-semibold text-slate-800">{wp?.associatedDealName}</p>
        </div>
        <div>
          <p className="text-xs text-slate-500 mb-1">Category</p>
          <span
            className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold"
            style={{ color: cat?.color, background: cat?.bg }}
          >
            {wp?.category}
          </span>
        </div>
        <div>
          <p className="text-xs text-slate-500 mb-1">Estimated Value</p>
          <p className="text-sm font-bold text-slate-800">{String.fromCharCode(163)}{wp?.estimatedValue}m</p>
        </div>
        <div>
          <p className="text-xs text-slate-500 mb-1">Target Award Date</p>
          <p className="text-sm font-semibold text-slate-800">
            {wp?.targetAwardDate ? new Date(wp?.targetAwardDate)?.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
          </p>
        </div>
        <div>
          <p className="text-xs text-slate-500 mb-1">Procurement Lead</p>
          <p className="text-sm font-semibold text-slate-800">{wp?.procurementLead}</p>
        </div>
        <div>
          <p className="text-xs text-slate-500 mb-1">Bidder Count</p>
          <p className="text-sm font-semibold text-slate-800">{wp?.bidderCount}</p>
        </div>
      </div>
      {wp?.notes && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
          <p className="text-xs font-semibold text-amber-700 mb-1">Notes</p>
          <p className="text-xs text-amber-800 leading-relaxed">{wp?.notes}</p>
        </div>
      )}
    </div>
  );
}

function TenderStatusTab({ wp }) {
  const currentIdx = STEPPER_STEPS?.indexOf(wp?.tenderStatus);
  const isCancelled = wp?.tenderStatus === 'Cancelled';
  const cfg = TENDER_STATUS_CONFIG?.[wp?.tenderStatus];

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <span className="text-sm text-slate-600">Current Status:</span>
        <span
          className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold border"
          style={{ color: cfg?.color, background: cfg?.bg, borderColor: cfg?.border }}
        >
          {wp?.tenderStatus}
        </span>
      </div>
      {!isCancelled && (
        <div className="flex items-center">
          {STEPPER_STEPS?.map((step, idx) => {
            const isCompleted = idx < currentIdx;
            const isActive = idx === currentIdx;
            return (
              <React.Fragment key={step}>
                <div className="flex flex-col items-center">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-all ${
                      isCompleted
                        ? 'bg-green-500 border-green-500 text-white'
                        : isActive
                        ? 'bg-[#3b4c4c] border-[#3b4c4c] text-white' :'bg-white border-slate-300 text-slate-400'
                    }`}
                  >
                    {isCompleted ? <Icon name="Check" size={14} /> : idx + 1}
                  </div>
                  <span className={`text-xs mt-1 font-medium whitespace-nowrap ${
                    isCompleted ? 'text-green-600' : isActive ? 'text-[#3b4c4c]' : 'text-slate-400'
                  }`}>
                    {step}
                  </span>
                </div>
                {idx < STEPPER_STEPS?.length - 1 && (
                  <div
                    className={`flex-1 h-0.5 mb-5 mx-1 ${idx < currentIdx ? 'bg-green-400' : 'bg-slate-200'}`}
                    style={{ minWidth: 24 }}
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>
      )}
      <div className="space-y-3">
        {[
          { label: 'Tender Issue Date', value: wp?.tenderIssueDate },
          { label: 'Submission Deadline', value: wp?.submissionDeadline },
          { label: 'Evaluation Period', value: wp?.evaluationPeriod },
          { label: 'Award Date', value: wp?.awardDate },
        ]?.map(({ label, value }) => (
          <div key={label} className="flex items-center justify-between py-2 border-b border-slate-100">
            <span className="text-sm text-slate-500">{label}</span>
            <span className="text-sm font-semibold text-slate-800">
              {value
                ? (value?.includes('to') ? value : new Date(value)?.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }))
                : <span className="text-slate-400 font-normal">Not set</span>}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function BidderShortlistTab({ wp }) {
  if (!wp?.bidders || wp?.bidders?.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mb-3">
          <Icon name="Users" size={20} color="#94a3b8" />
        </div>
        <p className="text-sm text-slate-500">No bidders shortlisted yet</p>
      </div>
    );
  }

  const submissionConfig = {
    Submitted: { color: '#059669', bg: 'rgba(5,150,105,0.1)' },
    Pending: { color: '#d97706', bg: 'rgba(217,119,6,0.1)' },
    Withdrawn: { color: '#dc2626', bg: 'rgba(220,38,38,0.1)' },
  };

  return (
    <div className="space-y-3">
      {wp?.bidders?.map((bidder) => {
        const sub = submissionConfig?.[bidder?.submissionStatus];
        return (
          <div key={bidder?.id} className="bg-slate-50 rounded-lg p-3 border border-slate-200">
            <div className="flex items-start justify-between gap-2 mb-2">
              <div>
                <p className="text-sm font-semibold text-slate-800">{bidder?.name}</p>
                <p className="text-xs text-slate-500">{bidder?.type}</p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {bidder?.rank && (
                  <span className="w-6 h-6 rounded-full bg-[#3b4c4c] text-white text-xs font-bold flex items-center justify-center">
                    {bidder?.rank}
                  </span>
                )}
                <span
                  className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                  style={{ color: sub?.color, background: sub?.bg }}
                >
                  {bidder?.submissionStatus}
                </span>
              </div>
            </div>
            {(bidder?.technicalScore !== null || bidder?.commercialScore !== null) && (
              <div className="flex items-center gap-4 mb-2">
                <div className="flex items-center gap-1">
                  <span className="text-xs text-slate-500">Technical:</span>
                  <span className="text-xs font-bold text-slate-800">{bidder?.technicalScore !== null ? bidder?.technicalScore : '—'}</span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-xs text-slate-500">Commercial:</span>
                  <span className="text-xs font-bold text-slate-800">{bidder?.commercialScore !== null ? bidder?.commercialScore : '—'}</span>
                </div>
              </div>
            )}
            {bidder?.notes && (
              <p className="text-xs text-slate-500 leading-relaxed">{bidder?.notes}</p>
            )}
          </div>
        );
      })}
    </div>
  );
}

function AwardDecisionTab({ wp, userRole, onAward }) {
  const isAwarded = wp?.tenderStatus === 'Awarded';

  if (!isAwarded) {
    return (
      <div className="space-y-4">
        <div className="flex flex-col items-center justify-center py-10 text-center">
          <div className="w-14 h-14 rounded-full bg-amber-50 flex items-center justify-center mb-3">
            <Icon name="Clock" size={24} color="#d97706" />
          </div>
          <p className="text-sm font-semibold text-slate-700 mb-1">Pending Award Decision</p>
          <p className="text-xs text-slate-500 max-w-xs">The award decision has not yet been made for this work package.</p>
        </div>
        {userRole === 'admin' && (
          <button
            onClick={onAward}
            className="w-full py-2.5 px-4 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
          >
            <Icon name="Award" size={16} />
            Record Award Decision
          </button>
        )}
        {userRole === 'sponsor' && (
          <p className="text-xs text-center text-slate-400">Only Admins can record award decisions.</p>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="bg-green-50 border border-green-200 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center">
            <Icon name="Award" size={16} color="white" />
          </div>
          <div>
            <p className="text-xs text-green-600 font-medium">Contract Awarded</p>
            <p className="text-sm font-bold text-green-800">{wp?.awardedBidder}</p>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <p className="text-xs text-slate-500">Award Value</p>
            <p className="text-sm font-bold text-slate-800">{String.fromCharCode(163)}{wp?.awardValue}m</p>
          </div>
          <div>
            <p className="text-xs text-slate-500">Award Date</p>
            <p className="text-sm font-semibold text-slate-800">
              {wp?.awardDate ? new Date(wp?.awardDate)?.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
            </p>
          </div>
          <div>
            <p className="text-xs text-slate-500">Approver</p>
            <p className="text-sm font-semibold text-slate-800">{wp?.approver}</p>
          </div>
          <div>
            <p className="text-xs text-slate-500">Approval Date</p>
            <p className="text-sm font-semibold text-slate-800">
              {wp?.approvalDate ? new Date(wp?.approvalDate)?.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
            </p>
          </div>
        </div>
      </div>
      {wp?.awardRationale && (
        <div>
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Award Rationale</p>
          <p className="text-sm text-slate-700 leading-relaxed">{wp?.awardRationale}</p>
        </div>
      )}
    </div>
  );
}

const TABS = [
  { id: 'overview', label: 'Overview', icon: 'FileText' },
  { id: 'tender', label: 'Tender Status', icon: 'ClipboardList' },
  { id: 'bidders', label: 'Bidder Shortlist', icon: 'Users' },
  { id: 'award', label: 'Award Decision', icon: 'Award' },
];

export default function WorkPackageDrawer({ wp, userRole, onClose, onAddToast }) {
  const [activeTab, setActiveTab] = useState('overview');

  if (!wp) return null;

  const handleAward = () => {
    onAddToast?.({ type: 'success', title: 'Award recorded', message: 'Award decision recorded for ' + wp?.name });
    onClose?.();
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/30 z-40" onClick={onClose} />
      <div
        className="fixed top-0 right-0 h-full w-full max-w-xl bg-white shadow-2xl z-50 flex flex-col"
        style={{ animation: 'slideInRight 0.25s ease-out' }}
      >
        <div className="flex items-start justify-between p-6 border-b border-slate-200 flex-shrink-0">
          <div className="flex-1 min-w-0 pr-4">
            <p className="text-xs text-slate-500 font-medium mb-1">Work Package</p>
            <h3 className="text-lg font-bold text-slate-900 leading-snug">{wp?.name}</h3>
            <p className="text-sm text-slate-500 mt-0.5">{wp?.associatedDealName}</p>
          </div>
          <button
            onClick={onClose}
            className="flex-shrink-0 p-2 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="flex border-b border-slate-200 flex-shrink-0 overflow-x-auto">
          {TABS?.map((tab) => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex items-center gap-1.5 px-4 py-3 text-xs font-semibold whitespace-nowrap border-b-2 transition-colors ${
                activeTab === tab?.id
                  ? 'border-[#3b4c4c] text-[#3b4c4c]' :'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              <Icon name={tab?.icon} size={14} />
              {tab?.label}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'overview' && <OverviewTab wp={wp} />}
          {activeTab === 'tender' && <TenderStatusTab wp={wp} />}
          {activeTab === 'bidders' && <BidderShortlistTab wp={wp} />}
          {activeTab === 'award' && <AwardDecisionTab wp={wp} userRole={userRole} onAward={handleAward} />}
        </div>
      </div>
    </>
  );
}
